import type { SupabaseClient } from '@supabase/supabase-js'

import { mergeTargetTitleOptions } from '../utils/targetTitleUtils'

export interface AccountTargetTitleVariant {
  key: string
  title: string
  sourceResumeId: string
  sourceResumeTitle: string
  sourceLabel: string
}

function normalizeTitleForDedupe(title: string): string {
  return title.trim().replace(/\s+/g, ' ').toLowerCase()
}

function variantKey(sourceResumeId: string, normalizedTitle: string): string {
  return `${sourceResumeId}:${normalizedTitle}`
}

export async function fetchAccountTargetTitleVariants(
  supabase: SupabaseClient,
  options: { currentResumeId?: string } = {},
): Promise<AccountTargetTitleVariant[]> {
  const { currentResumeId } = options

  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser()
  if (authError) throw authError
  if (!user) return []

  const { data: resumes, error: resumesError } = await supabase
    .from('resumes')
    .select('id, title, updated_at')
    .eq('user_id', user.id)
    .order('updated_at', { ascending: false })

  if (resumesError) throw resumesError

  const resumeRows = resumes ?? []
  if (resumeRows.length === 0) return []

  const titleById = new Map(resumeRows.map((r) => [r.id, r.title ?? 'Resume']))
  const resumeIds = resumeRows.map((r) => r.id)

  const { data: contentRows, error: contentError } = await supabase
    .from('resume_content')
    .select('resume_id, content')
    .in('resume_id', resumeIds)
    .eq('section_type', 'target_title')

  if (contentError) throw contentError

  const contentByResume = new Map<string, unknown>()
  for (const row of contentRows ?? []) {
    contentByResume.set(row.resume_id, row.content)
  }

  const seen = new Set<string>()
  const variants: AccountTargetTitleVariant[] = []

  const resumeIdOrder = currentResumeId
    ? [...resumeIds.filter((id) => id !== currentResumeId), currentResumeId]
    : resumeIds

  for (const resumeId of resumeIdOrder) {
    const content = contentByResume.get(resumeId)
    if (content === undefined) continue
    const resumeTitle = titleById.get(resumeId) ?? 'Resume'
    for (const title of mergeTargetTitleOptions(content)) {
      const trimmed = title.trim()
      const normalized = normalizeTitleForDedupe(trimmed)
      if (!normalized || seen.has(normalized)) continue
      seen.add(normalized)
      variants.push({
        key: variantKey(resumeId, normalized),
        title: trimmed,
        sourceResumeId: resumeId,
        sourceResumeTitle: resumeTitle,
        sourceLabel: resumeTitle,
      })
    }
  }

  return variants.sort((a, b) =>
    a.sourceLabel.localeCompare(b.sourceLabel, undefined, { sensitivity: 'base' }),
  )
}

export function adoptAccountTargetTitle(currentTitles: string[], variant: AccountTargetTitleVariant): string[] {
  const title = variant.title.trim()
  if (!title) return currentTitles
  const pool = mergeTargetTitleOptions(currentTitles)
  const merged = pool.includes(title) ? pool : [...pool, title]
  const others = merged.filter((t) => t !== title)
  return [title, ...others]
}
